import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { FilterOperator, FilterType, processFilterValue } from "@/lib/filters";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Filter, XIcon } from "lucide-react";
import { buildMultipleFilterValues } from "@/lib/filters/buildMultipleFilterValues";
import { ClearFilterIconButton } from "./ClearFilterIconButton";

interface EnumFilterProps {
  columnId: string;
  options: Readonly<string[]>;
  translationKey: string;
  placeholder?: string;
  renderOption?: (option: string, translation: string) => React.ReactNode;
}

export const EnumFilter = ({
  options,
  translationKey,
  columnId,
  placeholder = "table.choose",
  renderOption,
}: EnumFilterProps) => {
  const { t } = useTranslation();
  const { options: dataTableOptions } = useDataTableSettings();
  const isFilteredOrSorted = dataTableOptions.isFilteredOrSorted(columnId);
  const [open, setOpen] = useState(false);

  const translationMap = useMemo(() => {
    const map = new Map<string, string>();
    options.forEach((option) => {
      map.set(option, t(`${translationKey}.${option}`));
    });
    return map;
  }, [options, t, translationKey]);

  const columnFilterValue = useMemo(() => {
    const filterValue = dataTableOptions.getFilterValue(columnId);
    if (!filterValue || !filterValue.value) {
      return [];
    }
    return processFilterValue(filterValue.value);
  }, [columnId, dataTableOptions]);

  const onSelect = useCallback(
    (value: string) => {
      const nextFilterValues = columnFilterValue.includes(value)
        ? columnFilterValue.filter((v) => v !== value)
        : [...columnFilterValue, value];
      dataTableOptions.setFilteredMark(columnId, true);
      dataTableOptions.setFilterValue(columnId, {
        value: buildMultipleFilterValues(nextFilterValues, FilterOperator.OR),
        filter: FilterType.EqualsString,
      });
    },
    [columnId, dataTableOptions, columnFilterValue],
  );

  const onClear = useCallback(() => {
    dataTableOptions.setFilteredMark(columnId, false);
    dataTableOptions.setFilterValue(columnId, {
      value: "",
      filter: FilterType.EqualsString,
    });
    setOpen(false);
  }, [columnId, dataTableOptions]);

  const displayText = useMemo(() => {
    if (!columnFilterValue.length) {
      return t(placeholder);
    }
    return columnFilterValue
      .map((value) => t(`${translationKey}.${value}`))
      .join(", ");
  }, [columnFilterValue, placeholder, t, translationKey]);

  return (
    <div className="flex gap-1 items-center min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full min-w-0 justify-between p-2 pl-3 text-sm h-10 hover:bg-white",
              {
                "border-blue-500/50 text-blue-700 hover:text-blue-900 hover:bg-blue-200":
                  isFilteredOrSorted,
              },
            )}
          >
            <span className="flex flex-row gap-2 items-center overflow-hidden">
              <Filter className="h-4 w-4 shrink-0" />
              <span className="truncate">{displayText}</span>
            </span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="min-w-[200px] w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder={t("common.search")} />
            <CommandList>
              <CommandEmpty>{t("common.noResults")}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => {
                  const translation = translationMap.get(option) as string;
                  return (
                    <CommandItem
                      key={option}
                      value={option}
                      onSelect={onSelect}
                      keywords={[translation]}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          columnFilterValue.includes(option)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      {renderOption
                        ? renderOption(option, translation)
                        : translation}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {columnFilterValue.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem onSelect={onClear} className="gap-2">
                      <XIcon className="w-4 h-4" />
                      {t("common.clear")}
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ClearFilterIconButton
        onClick={onClear}
        visible={columnFilterValue.length > 0}
      />
    </div>
  );
};
